import { APP_BASE_HREF } from '@angular/common';
import {
  ApplicationConfig,
  computed,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import { provideDialogDefaultConfig } from './components/dialog/dialog-providers';
import { providePhoneInputConfig } from './components/phone-input/phone-input.config';
import {
  provideAppTitleStrategy,
  provideLocaleId,
  provideMatDateAdapter,
  provideMatDateFnsFormats,
  provideMatDateLocale,
  provideMatFormFieldDefaultOptions,
  provideMatIconDefaultOptions,
  provideMatSnackBarDefaultOptions,
  provideMatTooltipOptions,
} from './utils/providers';

import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import nlLocale from '@angular/common/locales/nl';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { nl } from 'date-fns/locale';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { catchError, of } from 'rxjs';
import {
  AppEnvironments,
  includeAllNgxFrontendToolsetInterceptors,
  LibInitService,
  LoggerLevel,
  provideNgxFrontendToolset,
  USER_PERMISSIONS,
  UserService,
} from '@ipg/ngx-frontend-toolset';
import { environment } from '../environments/environment';
import { DutchPaginatorIntl } from './services/dutch-paginator-intl';
import { permissionSyncInterceptor } from './interceptors/permission-sync.interceptor';
import { clientMoveInterceptor } from './interceptors/client-move.interceptor';

registerLocaleData(nlLocale);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([
        ...includeAllNgxFrontendToolsetInterceptors(),
        permissionSyncInterceptor,
        clientMoveInterceptor,
      ])
    ),
    provideNgxFrontendToolset({
      activeEnvironment: environment.activeEnvironment,
      environmentConfig: [
        {
          environment: AppEnvironments.DEV,
          apiUrl: new URL('http://localhost:8081/'),
        },
        {
          environment: AppEnvironments.STAG,
          apiUrl: new URL('https://stag.api.robotctrl.com/'),
        },
        {
          environment: AppEnvironments.PROD,
          apiUrl: new URL('https://v3.api.robotctrl.com/'),
          loggerLevel: LoggerLevel.WARN,
        },
      ],
      loggerLevel: LoggerLevel.DEBUG,
      dbConfig: {
        dbName: 'robotctrl-db',
        dbVersion: 1,
      },
      routeConfig: {
        loginRoute: '/login',
        resetPasswordRoute: '/reset-password',
        reportRoute: '/error-report',
        autoRedirectOnInitError: false, // Disable auto-redirect to handle tenant errors
      },
    }),
    provideAppInitializer(() => {
      const libInit = inject(LibInitService);
      const router = inject(Router);
      let associatedName = '';

      // Check if we're on a standalone route (enter-organization, organization-not-found, error-report)
      const standaloneRoutes = [
        '/enter-organization',
        '/organization-not-found',
        '/error-report',
      ];
      const isStandaloneRoute = standaloneRoutes.some((route) =>
        location.pathname.startsWith(route)
      );

      if (isStandaloneRoute) {
        // Don't initialize the library for standalone routes
        // These routes don't need tenant context
        return of(void 0);
      }

      // Extract associated name from the first path segment
      // e.g., dashboard.robotctrl.com/ASSOCIATED_NAME/clients -> ASSOCIATED_NAME
      associatedName = location.pathname.split('/').filter((s) => s)[0] || '';

      if (!associatedName) {
        // No tenant in URL, redirect to tenant entry page
        router.navigate(['/enter-organization']);
        return of(void 0);
      }

      // Initialize with the associated name from URL
      return libInit
        .init({
          associatedName,
        })
        .pipe(
          catchError((error) => {
            console.error('Library initialization failed:', error);

            // Check if this is a tenant lookup error
            if (
              error?.error?.detail?.errorCode ===
              'TENANT_LOOKUP_ASSOCIATED_NAME_UNKNOWN'
            ) {
              // Navigate to organization-not-found page
              router.navigate(['/organization-not-found'], {
                queryParams: { attemptedName: associatedName },
              });
            } else {
              // Other errors go to error-report page
              router.navigate(['/error-report']);
            }

            return of(void 0);
          })
        );
    }),

    // Set dynamic base href based on associatedName
    // This ensures all relative navigations automatically include the associatedName prefix
    {
      provide: APP_BASE_HREF,
      useFactory: () => {
        // Check if we're on a standalone route
        const standaloneRoutes = [
          '/enter-organization',
          '/organization-not-found',
          '/error-report',
        ];
        const isStandaloneRoute = standaloneRoutes.some((route) =>
          window.location.pathname.startsWith(route)
        );

        if (isStandaloneRoute) {
          // For standalone routes, use root as base
          return '/';
        }

        // Extract associatedName from URL
        const associatedName =
          window.location.pathname.split('/').filter((s) => s)[0] || '';

        return associatedName ? `/${associatedName}/` : '/';
      },
    },

    // Custom providers
    provideAppTitleStrategy(),
    provideDialogDefaultConfig({}),
    providePhoneInputConfig({
      onlyCountries: ['NL'],
      defaultCountry: 'NL',
    }),
    provideMatFormFieldDefaultOptions({ appearance: 'outline' }),
    provideMatIconDefaultOptions({ fontSet: 'material-symbols-outlined' }),
    provideMatTooltipOptions({ showDelay: 300 }),
    provideMatSnackBarDefaultOptions({
      duration: 5000,
    }),

    provideLocaleId('nl'),
    provideMatDateLocale(nl),
    provideMatDateFnsFormats(),
    provideMatDateAdapter(DateFnsAdapter),

    provideCharts(withDefaultRegisterables()),
    {
      provide: MatPaginatorIntl,
      useClass: DutchPaginatorIntl,
    },
    {
      provide: USER_PERMISSIONS,
      useFactory: () => {
        const userService = inject(UserService);
        return computed(() => userService.data()?.permissions ?? null);
      },
    },
  ],
};
